import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import PageTitle from '@/components/PageTitle/PageTitle';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogDescription,
    getCatalogSellerId,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { safelyRender } from '@/utils/safelyRender';

import CatalogDetails from '../Details/CatalogDetails';
import SellerInfo from '../SellerInfo/SellerInfo';

type Props = {
    catalogId: number;
    forceIsLive: boolean;
};

const CatalogInfo = ({ catalogId, forceIsLive }: Props) => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();

    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);

    return (
        <CatalogInfoContainer>
            {!isMobile && (
                <CatalogDetails
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                />
            )}
            <TitleContainer>
                <PageTitle title={title} />
            </TitleContainer>
            <SellerInfo sellerId={sellerId} />
            {isMobile && (
                <CatalogDetails
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                />
            )}
            <Description>
                <ViewMore>
                    <BodyPrimary>{safelyRender({ html: description })}</BodyPrimary>
                </ViewMore>
            </Description>
        </CatalogInfoContainer>
    );
};

export default React.memo<Props>(CatalogInfo);

const CatalogInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 calc(100% - 290px);
    background-color: ${({ theme }) => theme.colors.white};
    margin-right: 16px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
        margin: 0;
        max-width: 100%;
        padding-left: 0;
        flex: 0 1 auto;
        flex-direction: column;
    }
`;

const TitleContainer = styled.div`
    && {
        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            section {
                justify-content: center;
            }
        }
        h1 {
            text-align: left;
            margin: 32px 0 16px;
            @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
                text-align: center;
            }
        }
    }
`;

const Description = styled.div`
    margin-right: 16px;
    margin-bottom: 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin: 0 0 32px 0;
        text-align: center;
    }
`;
